/**
 * Trunnion AI - Tool Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/config/queryKeys';
import { toolsApi } from '@/api/tools.api';
import { useNotificationStore } from '@/stores/notificationStore';
import type { CreateToolRequest, UpdateToolRequest } from '@/types';

export function useToolList(filters: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: queryKeys.tools.list(filters),
    queryFn: () => toolsApi.list(filters),
  });
}

export function useTool(id: string) {
  return useQuery({
    queryKey: queryKeys.tools.detail(id),
    queryFn: () => toolsApi.getById(id),
    enabled: !!id,
  });
}

export function useCreateTool() {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: (tool: CreateToolRequest) => toolsApi.create(tool),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tools.all });
      addNotification({
        type: 'success',
        title: 'Tool Registered',
        message: 'Tool has been registered successfully.',
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Registration Failed',
        message: error.message,
      });
    },
  });
}

export function useUpdateTool() {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateToolRequest }) =>
      toolsApi.update(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tools.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tools.detail(variables.id),
      });
      addNotification({
        type: 'success',
        title: 'Tool Updated',
        message: 'Tool has been updated successfully.',
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Update Failed',
        message: error.message,
      });
    },
  });
}

export function useDeleteTool() {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: (id: string) => toolsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tools.all });
      addNotification({
        type: 'success',
        title: 'Tool Deleted',
        message: 'Tool has been removed from the registry.',
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Delete Failed',
        message: error.message,
      });
    },
  });
}
